<script setup lang="ts">
import BaseTabs from "@/components/ui/BaseTabs.vue";
import {
  useUserStore,
  useUserFilesStore,
  useLegalStore,
  useProfileStore,
  useEventsStore,
  useNetworksStore,
  useApplicationsStore,
  useAssignmentsStore,
  useRatingStore,
  useRecommendationStore,
  useDashboardStore,

} from "@/stores";
import {storeToRefs} from "pinia";

import {useAdminHandler} from "@/composables/useAdminHandler";

const dashboardStore = useDashboardStore();
const { latestData } = storeToRefs(dashboardStore);

const userStore = useUserStore();
const { usersById, myUserId, currentUserId, myUser, currentUser } = storeToRefs(userStore);

const userFilesStore = useUserFilesStore();
const { myFiles } = storeToRefs(userFilesStore);

const profileStore = useProfileStore();
const {profilesById, myProfileId, currentProfileId, myProfile, currentProfile } = storeToRefs(profileStore);
const eventsStore = useEventsStore();
const {
  eventsById,
  myEventIds,
  currentEventId,
  publicEventIds,
  networkEventsMeta,
  currentEvent,
  myEvents,
  publicEvents,
  networkEventsFor,
  myAvailableEvents

} = storeToRefs(eventsStore);

const networksStore = useNetworksStore();
const { networksById, publicNetworkIds, myNetworkIds, currentNetworkId, eventNetworksMeta, currentNetwork, myNetworks } = storeToRefs(networksStore);

const applicationsStore = useApplicationsStore();
const { applicationsById, myApplicationIds, currentApplicationId, currentApplication, myApplications, eventApplications, eventApplicants } = storeToRefs(applicationsStore);

const assignmentsStore = useAssignmentsStore();
const { assignmentsById, myAssignmentIds, myAssignmentId, myAssignments, currentAssignment } = storeToRefs(assignmentsStore);

const ratingStore = useRatingStore();
const { ratingsById, myRatingId, myRatingReceivedIds, myRatingGivenIds, publicRatingByEventIds, publicRatingByProfileIds, myRating, myRatingsReceived, myRatingsGiven, getPublicRatingsByType } = storeToRefs(ratingStore);


const legalStore = useLegalStore();
const { currentLegal } = storeToRefs(legalStore);

const recommendationStore = useRecommendationStore();
const { recommendationsById, myRecommendationIds, myRecommendations } = storeToRefs(recommendationStore);

const { confirmCron, handlingInProgress, confirmReset, confirmPush } = useAdminHandler();

</script>

<template>
  <BaseTabs :tabs="[
    { label: 'Overview', value: 'overview' },
    { label: 'Dashboard', value: 'dashboard' },
    { label: 'User', value: 'user' },
    { label: 'Legal', value: 'legal' },
    { label: 'Profile', value: 'profile' },
    { label: 'Events', value: 'events' },
    { label: 'Networks', value: 'networks' },
    { label: 'Applications', value: 'applications' },
    { label: 'Assignments', value: 'assignments' },
    { label: 'Ratings', value: 'ratings' },
    { label: 'Recommendations', value: 'recommendations' }
  ]">
    <template #default="{ current }">
      <div v-if="current === 'overview'">
        <h1>Overview</h1>

        <h2>CronJobs</h2>
        <div class="flex flex-wrap gap-4 mt-4">
          <button
            @click="confirmCron('users')"
            class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded shadow transition"
          >
            🗑️ Delete Users
          </button>

          <button
            @click="confirmCron('events')"
            class="bg-orange-600 hover:bg-orange-700 text-white font-medium px-4 py-2 rounded shadow transition"
          >
            🗑️ Delete Events
          </button>

          <button
            @click="confirmCron('assignments')"
            class="bg-yellow-600 hover:bg-yellow-700 text-white font-medium px-4 py-2 rounded shadow transition"
          >
            🗑️ Delete Assignments
          </button>

          <button
            @click="confirmCron('networks')"
            class="bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-medium px-4 py-2 rounded shadow transition"
          >
            🗑️ Delete Networks
          </button>
        </div>

        <h2>Tools</h2>
        <div class="flex flex-wrap gap-4 mt-4">
          <button
            @click="confirmReset()"
            class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded shadow transition"
          >
            🧹 RESET DB
          </button>

          <button
            @click="confirmPush()"
            class="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded shadow transition"
          >
            Send Push Notifications
          </button>
        </div>


      </div>
      <div v-if="current === 'dashboard'">
        <h1>Dashboard</h1>
        <h2>latestData</h2>
        <pre>{{ latestData }}</pre>
      </div>

      <div v-if="current === 'user'">
        <h1>User</h1>


        <h2>myUserId</h2>
        <pre>{{ myUserId }}</pre>

        <h2>currentUserId</h2>
        <pre>{{ currentUserId }}</pre>

        <h2>usersById</h2>
        <pre>{{ usersById }}</pre>


        <hr class="my-4">

        <h1>Getters</h1>
        <h2>myUser</h2>
        <pre>{{ myUser }}</pre>

        <h2>myFiles</h2>
        <pre>{{ myFiles }}</pre>

        <h2>currentUser</h2>
        <pre>{{ currentUser }}</pre>
      </div>

      <div v-if="current === 'legal'">
        <h1>Legal</h1>
        <h2>currentLegal</h2>
        <pre>{{ currentLegal }}</pre>
      </div>

      <div v-if="current === 'profile'">
        <h1>Profile</h1>

        <h2>myProfileId</h2>
        <pre>{{ myProfileId }}</pre>

        <h2>currentProfileId</h2>
        <pre>{{ currentProfileId }}</pre>

        <h2>profilesById</h2>
        <pre>{{ profilesById }}</pre>

        <hr class="my-4">

        <h1>Getters</h1>

        <h2>myProfile</h2>
        <pre>{{ myProfile }}</pre>

        <h2>currentProfile</h2>
        <pre>{{ currentProfile }}</pre>

      </div>

      <div v-if="current === 'events'">
        <h1>Events</h1>

        <h2>currentEventId</h2>
        <pre>{{ currentEventId }}</pre>

        <h2>myEventIds</h2>
        <pre>{{ myEventIds }}</pre>

        <h2>publicEventIds</h2>
        <pre>{{ publicEventIds }}</pre>

        <h2>networkEventsMeta (events by networkId)</h2>
        <pre>{{ networkEventsMeta }}</pre>

        <h2>eventsById</h2>
        <pre>{{ eventsById }}</pre>


        <hr class="my-4">

        <h1>Getters</h1>

        <h2>currentEvent</h2>
        <pre>{{ currentEvent }}</pre>

        <h2>myEvents</h2>
        <pre>{{ myEvents }}</pre>

        <h2>publicEvents</h2>
        <pre>{{ publicEvents }}</pre>

        <h2>myAvailableEvents</h2>
        <pre>{{ myAvailableEvents }}</pre>
        <!--
        <h2>networkEventsFor</h2>
        <pre>{{ networkEventsFor }}</pre>
        -->

      </div>

      <div v-if="current === 'networks'">
        <h1>Networks</h1>

        <h2>currentNetworkId</h2>
        <pre>{{ currentNetworkId }}</pre>

        <h2>myNetworkIds</h2>
        <pre>{{ myNetworkIds }}</pre>

        <h2>publicNetworkIds</h2>
        <pre>{{ publicNetworkIds }}</pre>

        <h2>eventNetworksMeta (networks by eventId)</h2>
        <pre>{{ eventNetworksMeta }}</pre>

        <h2>networksById</h2>
        <pre>{{ networksById }}</pre>

        <hr class="my-4">
        <h1>Getters</h1>

        <h2>currentNetwork</h2>
        <pre>{{ currentNetwork }}</pre>

        <h2>myNetworks</h2>
        <pre>{{ myNetworks }}</pre>
      </div>

      <div v-if="current === 'applications'">
        <h1>Applications</h1>
        <h2>currentApplicationId</h2>
        <pre>{{ currentApplicationId }}</pre>

        <h2>myApplicationIds</h2>
        <pre>{{ myApplicationIds }}</pre>



        <h2>eventApplicants (eventId with applicants)</h2>
        <pre>{{ eventApplicants }}</pre>

        <h2>eventApplications</h2>
        <pre>{{ eventApplications }}</pre>

        <h2>applicationsById</h2>
        <pre>{{ applicationsById }}</pre>

        <hr class="my-4">

        <h1>Getters</h1>
        <h2>myApplications</h2>
        <pre>{{ myApplications }}</pre>

        <h2>currentApplication</h2>
        <pre>{{ currentApplication }}</pre>


      </div>

      <div v-if="current === 'assignments'">
        <h1>Assignments</h1>

        <h2>myAssignmentId</h2>
        <pre>{{ myAssignmentId }}</pre>

        <h2>myAssignmentIds</h2>
        <pre>{{ myAssignmentIds }}</pre>

        <h2>assignmentsById</h2>
        <pre>{{ assignmentsById }}</pre>


        <hr class="my-4">

        <h1>Getters</h1>
        <h2>myAssignments</h2>
        <pre>{{ myAssignments }}</pre>

        <h2>currentAssignment</h2>
        <pre>{{ currentAssignment }}</pre>

      </div>

      <div v-if="current === 'ratings'">

        <h1>Ratings</h1>

        <h2>myRatingId</h2>
        <pre>{{ myRatingId }}</pre>

        <h2>myRatingReceivedIds</h2>
        <pre>{{ myRatingReceivedIds }}</pre>

        <h2>myRatingGivenIds</h2>
        <pre>{{ myRatingGivenIds }}</pre>

        <h2>publicRatingByEventIds</h2>
        <pre>{{ publicRatingByEventIds }}</pre>

        <h2>publicRatingByProfileIds</h2>
        <pre>{{ publicRatingByProfileIds }}</pre>

        <h2>ratingsById</h2>
        <pre>{{ ratingsById }}</pre>

        <hr class="my-4">

        <h1>Getters</h1>

        myRating, myRatingsReceived, myRatingsGiven, getPublicRatingsByType

        <h2>myRating</h2>
        <pre>{{ myRating }}</pre>

        <h2>myRatingsReceived</h2>
        <pre>{{ myRatingsReceived }}</pre>

        <h2>myRatingsGiven</h2>
        <pre>{{ myRatingsGiven }}</pre>
      </div>

      <div v-if="current === 'recommendations'">

        <h1>Recommendations</h1>

        <h2>recommendationsById</h2>
        <pre>{{ recommendationsById }}</pre>

        <h2>myRecommendationIds</h2>
        <pre>{{ myRecommendationIds }}</pre>

        <hr class="my-4">
        <h1>Getters</h1>
        <h2>myRecommendations</h2>
        <pre>{{ myRecommendations }}</pre>
      </div>


    </template>
  </BaseTabs>
</template>

<style scoped>
h1 {
  font-size: 1.5rem;
  font-weight: bold;
}
h2 {
  margin-top: 1rem;
  font-size: 1rem;
  font-weight: bold;
}
</style>
